import { Checkbox, DatePicker, Form, FormInstance, Select } from 'antd'
import { useAtom } from 'jotai'
import { isNumber, toNumber } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Editor, UsersSearch } from '@/components'
import { PriorityPopoverDescription } from '@/components/priority-popover.component'
import { usersMapAtom } from '@/modules/user'
import { PriorityEnum, ProjectClassification, ProjectType, ReleaseType } from '@/schemas'
import { removeHtmlTag } from '@/utils/remove-html-tag'

import { ProjectNameInput } from './project-name-input.component'

const { RangePicker } = DatePicker

export interface ProjectValues {
  name: string
  email: string
  range?: any[]
  departmentId: number
  source: string
  industry: [number, number]
  classification: number
  priority: PriorityEnum
  challenge: string
  projectType: ProjectType
  releaseNumber: string | null
  releaseJiraKey: string | null
  releaseType: ReleaseType | null
}

export enum ProjectFormType {
  CREATE = 'create',
  CLONE = 'clone',
  EDIT = 'edit',
}
interface ProjectFormProps {
  form: FormInstance<ProjectValues>
  onSubmit: (data: ProjectValues) => void
  selfId?: string
  jql?: string
  type?: ProjectFormType
}

export interface DepartmentUser {
  id: string
  avatar: string
  name: string
}

export const classificationOptionsRaw = Object.entries(ProjectClassification)
  .filter(([value]) => isNumber(Number(value)) && !isNaN(Number(value)))
  .map(([value, key]) => ({
    value: Number(value),
    label: key,
  }))

// 调整展示顺序
export const classificationOptions = classificationOptionsRaw.slice(1).concat(classificationOptionsRaw.slice(0, 1))

export const ProjectForm: React.FC<ProjectFormProps> = ({ selfId, form, onSubmit, jql, children, type }) => {
  const { t } = useTranslation()
  const [usersMap] = useAtom(usersMapAtom)
  // const [releaseVisible, { toggle: releaseToggle }] = useToggle(
  //   isUndefined(form.getFieldValue('projectType')) ? false : form.getFieldValue('projectType') === ProjectType.RELEASE
  // )

  const isClone = type === ProjectFormType.CLONE

  const onFinish = () => {
    form
      .validateFields()
      .then((data) => {
        const newJql = selfId
          ? jql
            ? undefined
            : data.releaseJiraKey
              ? `issue in linkedIssues(${data.releaseJiraKey})`
              : undefined
          : data.releaseJiraKey
            ? `issue in linkedIssues(${data.releaseJiraKey})`
            : undefined

        const startDate = data.range && data.range[0].format('YYYY-MM-DD')
        const endDate = data.range && data.range[1].format('YYYY-MM-DD')
        delete data.range

        onSubmit({
          ...data,
          industryId: data.industry?.[0],
          scenarioId: data.industry?.[1],
          startDate,
          endDate,
          ...(newJql && { jql: newJql }),
        })
      })
      .catch((err) => {
        console.error(err)
      })
  }

  const onValuesChange = (changed: Partial<ProjectValues>) => {
    const email = changed.email
    if (email && email in usersMap) {
      form.setFields([{ name: 'departmentId', value: usersMap[email].departmentIds[0] }])
    }
    // const projectType = changed.projectType
    // if (!isNil(projectType)) {
    //   releaseToggle(projectType === ProjectType.RELEASE)
    // }
  }

  return (
    <div className='flex flex-wrap'>
      <div className='w-500px'>
        <Form
          name='basic'
          layout='horizontal'
          labelCol={{ span: 24 }}
          form={form}
          onValuesChange={onValuesChange}
          onFinish={onFinish}
        >
          <ProjectNameInput selfId={selfId} type='project' />

          <Form.Item label={t('project.owner')} name='email' rules={[{ required: true }]}>
            <UsersSearch placeholder={t('search users')} />
          </Form.Item>

          <Form.Item required label={t('cycle')} name='range' rules={[{ required: true }]}>
            <RangePicker format='YYYY-MM-DD' />
          </Form.Item>

          <Form.Item
            label={t('devlop.detail.classification')}
            required
            name='classification'
            rules={[{ required: true }]}
          >
            <Select placeholder={t('devlop.detail.classification')} options={classificationOptions}></Select>
          </Form.Item>
          <Form.Item
            label={t('department')}
            name='departmentId'
            className='hidden'
            rules={[{ required: true }]}
          ></Form.Item>

          <Form.Item
            label={t('resource.background')}
            tooltip={t('devlop.form.background.tooltip')}
            valuePropName='content'
            trigger='onChange'
            name='background'
            rules={[
              {
                required: true,
                validator: async (_, value) =>
                  !removeHtmlTag(value).trim()
                    ? Promise.reject(`${t('resource.background')}是必选填项!`)
                    : Promise.resolve(),
              },
            ]}
          >
            <Editor disableAutoSaveStyle className={'editorBorder'} />
          </Form.Item>
          <Form.Item
            label={t('resource.goalDesc')}
            tooltip={t('devlop.form.goal.tooltip')}
            valuePropName='content'
            trigger='onChange'
            name='goalDescription'
            rules={[
              {
                required: true,
                validator: async (_, value) =>
                  !removeHtmlTag(value).trim()
                    ? Promise.reject(`${t('resource.goalDesc')}是必选填项!`)
                    : Promise.resolve(),
              },
            ]}
          >
            <Editor disableAutoSaveStyle className={'editorBorder'} />
          </Form.Item>

          <Form.Item
            label={t('resource.checkStandard')}
            tooltip={t('devlop.form.standard.tooltip')}
            valuePropName='content'
            trigger='onChange'
            name='checkStandard'
            rules={[
              {
                required: true,
                validator: async (_, value) =>
                  !removeHtmlTag(value).trim()
                    ? Promise.reject(`${t('resource.checkStandard')}是必选填项!`)
                    : Promise.resolve(),
              },
            ]}
          >
            <Editor disableAutoSaveStyle className={'editorBorder'} />
          </Form.Item>

          <Form.Item
            label={t('resource.challenge')}
            tooltip={t('resource.challengeTooltip')}
            valuePropName='content'
            trigger='onChange'
            name='challenge'
            rules={[
              {
                required: true,
                validator: async (_, value) =>
                  !removeHtmlTag(value).trim()
                    ? Promise.reject(`${t('resource.challenge')}是必选填项!`)
                    : Promise.resolve(),
              },
            ]}
          >
            <Editor disableAutoSaveStyle className={'editorBorder'} />
          </Form.Item>

          <Form.Item
            label={
              <span>
                {t('devlop.detail.priority')}
                <PriorityPopoverDescription />
              </span>
            }
            name='priority'
            rules={[{ required: true }]}
          >
            <Select
              options={Object.keys(PriorityEnum)
                .filter((v) => isNaN(toNumber(v)))
                .map((key) => ({
                  label: t(`form.priority.${key}`),
                  value: PriorityEnum[key],
                }))}
            />
          </Form.Item>
          {isClone && (
            <Form.Item
              valuePropName='checked'
              label={t('project.clone.form.userRole.title')}
              name='cloneUserRole'
              rules={[{ required: true }]}
            >
              <Checkbox>{t('project.clone.userRole.title')}</Checkbox>
            </Form.Item>
          )}

          {/* {releaseVisible && (
            <>
              <Form.Item label={t('release number')} name='releaseNumber' rules={[{ required: true }]}>
                <Input allowClear placeholder='3.6.2' />
              </Form.Item>
              <Form.Item label={t('release type')} name='releaseType' rules={[{ required: true }]}>
                <Select
                  options={Object.keys(ReleaseType).map((key) => ({
                    label: t(`release type ${key}`),
                    value: key,
                  }))}
                ></Select>
              </Form.Item>
              <ProjectJiraKeyInput />
            </>
          )} */}

          <Form.Item wrapperCol={{ span: 8, offset: 0 }} className='mt-32px'>
            {children}
          </Form.Item>
        </Form>
      </div>
    </div>
  )
}
